import { useState, useEffect, useCallback } from 'react'

import type { CardSize, BreakpointKey, BreakpointConfig, ResponsiveConfig } from '@/types/dashboard-types'

// centeralized responsiveness for our configurable cards
export const responsiveConfig: ResponsiveConfig = {
  xs: {
    // Mobile: All cards full-width
    compressAt: 'tiny',
    tiny: 12,
    mini: 12,
    small: 12,
    medium: 12,
    large: 12,
    full: 12,
  },
  sm: {
    // Small tablets: small+ cards become compressed
    compressAt: 'small',
    tiny: 12,
    mini: 12,
    small: 12,
    medium: 24,
    large: 24,
    full: 24,
  },
  md: {
    // Medium screens: mini+ cards become compressed
    compressAt: 'mini',
    tiny: 8,
    mini: 12,
    small: 24,
    medium: 24,
    large: 24,
    full: 24,
  },
  lg: {
    // Large screens: only tiny cards become compressed
    compressAt: 'mini',
    tiny: 4,
    mini: 6,
    small: 8,
    medium: 12,
    large: 18,
    full: 24,
  },
  xl: {
    // Extra large: compression at tiny, all other cards display normally
    compressAt: 'tiny',
    tiny: 4,
    mini: 6,
    small: 8,
    medium: 12,
    large: 18,
    full: 24,
  },
  xxl: {
    // Extra large: no compression at tiny, all other cards display normally
    compressAt: 'tiny',
    tiny: 4,
    mini: 6,
    small: 8,
    medium: 12,
    large: 18,
    full: 24,
  },
}

export function getCurrentBreakpoint(): BreakpointKey {
  if (window.matchMedia('(max-width: 639px)').matches) return 'xs'
  if (window.matchMedia('(min-width: 640px) and (max-width: 767px)').matches) return 'sm'
  if (window.matchMedia('(min-width: 768px) and (max-width: 1279px)').matches) return 'md'
  if (window.matchMedia('(min-width: 1280px) and (max-width: 1639px)').matches) return 'lg'
  if (window.matchMedia('(min-width: 1640px) and (max-width: 1920px)').matches) return 'xl'
  return 'xxl'
}

// The compression logic
const sizeHierarchy: CardSize[] = ['tiny', 'mini', 'small', 'medium', 'large', 'full']

export function shouldCompress(cardSize: CardSize, breakpointConfig: BreakpointConfig): boolean {
  if (breakpointConfig.compressAt === 'none') return false

  const cardIndex = sizeHierarchy.indexOf(cardSize)
  const compressIndex = sizeHierarchy.indexOf(breakpointConfig.compressAt)

  return cardIndex <= compressIndex
}

export function getColumnSpan(cardSize: CardSize, breakpointConfig: BreakpointConfig): string {
  const cols = breakpointConfig[cardSize]
  return `span ${cols}`
}

export function useResponsive() {
  const [currentBreakpoint, setCurrentBreakpoint] = useState<BreakpointKey>('xl')

  useEffect(() => {
    const updateBreakpoint = () => {
      setCurrentBreakpoint(getCurrentBreakpoint())
    }

    updateBreakpoint()
    window.addEventListener('resize', updateBreakpoint)
    return () => window.removeEventListener('resize', updateBreakpoint)
  }, [])

  const currentConfig = responsiveConfig[currentBreakpoint]

  const calculatePixelWidth = useCallback(
    (cardSize: CardSize, containerRef: React.RefObject<HTMLElement>) => {
      if (!containerRef.current) return 0

      const containerWidth = containerRef.current.offsetWidth
      const gap = 20
      const maxCols = currentBreakpoint === 'xs' || currentBreakpoint === 'sm' ? 12 : 24
      const availableWidth = containerWidth - gap * (maxCols - 1)
      const colWidth = availableWidth / maxCols
      const span = currentConfig[cardSize]

      return colWidth * span + gap * (span - 1)
    },
    [currentBreakpoint, currentConfig]
  )

  return {
    currentBreakpoint,
    currentConfig,
    shouldCompress: (cardSize: CardSize) => shouldCompress(cardSize, currentConfig),
    getColumnSpan: (cardSize: CardSize) => getColumnSpan(cardSize, currentConfig),
    calculatePixelWidth,
  }
}
